/**
 * components/print/SuratJalanDialog.js
 * Colly capture for a Surat Jalan (PRD §6) — an add/remove row editor over
 * `txn.packing.suratJalanRows` (`{quantity, description}[]`). Standard app-modal shell
 * (`useModalEsc`, `.modal-overlay`/`.modal-box`), NOT the print-portal pattern — this is an
 * ordinary data-entry form, matching `PengepakanModal`'s own shell before it (T2c-2 retires
 * that component; this dialog is one of the two places its fields moved to).
 *
 * **Deliberately named generically, not `SuratJalanPusatDialog`** — PRD §5.5: "The two surat
 * jalan carry identical data — only the column headers differ... so one row editor serves
 * both." Only `SuratJalanPusat.js` (the pusat document) exists yet (T2c-2); Invoice/Surat
 * Jalan Cabang are T2d, out of this session's scope — but this dialog is already shaped to be
 * reused unmodified when that session builds Surat Jalan Cabang, rather than needing a rename.
 *
 * Row-editor UI mirrors `TransactionFormModal`'s own payment-rows convention exactly (a
 * `.card` with a `+ Tambah Baris` `btn btn-sm` and a per-row `✕` `btn btn-ghost btn-sm`), the
 * established add/remove-row pattern in this codebase — not a new one invented here.
 */
import React, { useState } from "react";
import { useModalEsc } from "../../utils/modalStack";

export default function SuratJalanDialog({ txn, onSubmit, onClose }) {
  const [rows, setRows] = useState(() => {
    const existing = txn.packing?.suratJalanRows || [];
    return existing.length > 0 ? existing.map((r) => ({ ...r })) : [{ quantity: "", description: "" }];
  });

  useModalEsc(onClose);

  const addRow = () => setRows((rs) => [...rs, { quantity: "", description: "" }]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const setRowField = (i, field, value) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const submit = (e) => {
    e.preventDefault();
    const suratJalanRows = rows
      .map((r) => ({ quantity: r.quantity.trim(), description: r.description.trim() }))
      .filter((r) => r.quantity !== "" || r.description !== "");
    // txn.lines cloned UNCHANGED — this dialog never touches collyNo, so saving here can never
    // clobber colly numbers the Checklist dialog already saved for the same transaction (both
    // write through the identical savePacking(id, {lines, packing}) shape).
    const lines = (txn.lines || []).map((l) => ({ ...l }));
    onSubmit({ lines, packing: { suratJalanRows } });
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <form className="modal-box" onSubmit={submit}>
        <h2 className="modal-title">Data Surat Jalan</h2>
        <div className="page-sub mono" style={{ marginBottom: 14 }}>
          {txn.invoiceNumber || txn.supplierInvoiceNo || txn.id}
        </div>

        <div className="card">
          <div className="card__title">Colly</div>
          {rows.map((r, i) => (
            <div className="payment-row" key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                <div className="form-row" style={{ flex: "0 0 100px", marginBottom: 0 }}>
                  <label className="form-label">Quantity</label>
                  <input className="form-input" value={r.quantity}
                    onChange={(e) => setRowField(i, "quantity", e.target.value)} />
                </div>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Keterangan</label>
                  <input className="form-input" value={r.description}
                    onChange={(e) => setRowField(i, "description", e.target.value)} />
                </div>
                <button type="button" className="btn btn-ghost btn-sm" title="Hapus baris" onClick={() => removeRow(i)}>✕</button>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={addRow}>+ Tambah Baris</button>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="submit" className="btn btn-primary">Simpan &amp; Cetak</button>
        </div>
      </form>
    </div>
  );
}
